import React, { useEffect, useState } from 'react';
import styles from './TextField.module.css';
import { withFloatingLabel } from '../FloatingLabel/withFloatingLabel.jsx';
import Spinner from '../../Icons/Spinner.jsx';

const DEFAULT_ASYNC_VALIDATE_ERROR_MESSAGE = 'Something went wrong, please try again';

/**
 * A simple, themeable text input with an optional label and helper/error text.
 *
 * @component
 * @param {Object} props
 * @param {import('react').ReactNode} [props.label] - Optional label rendered above the input.
 * @param {string|number} [props.value] - The input value (controlled).
 * @param {(value: string, event: import('react').ChangeEvent<HTMLInputElement>) => void} [props.onChange] - Called with the new string value.
 * @param {string} [props.placeholder] - Placeholder text.
 * @param {string} [props.type='text'] - The native input type.
 * @param {'small'|'medium'} [props.size='medium'] - Input height: small = 34px, medium = 40px.
 * @param {boolean} [props.disabled=false] - Disables the input.
 * @param {boolean} [props.fullWidth=false] - Stretch to the container width.
 * @param {boolean|string} [props.error] - Marks the field invalid; if a string, shown as error text (takes priority over `helperText`). Takes precedence over an in-flight `asyncValidate` result.
 * @param {import('react').ReactNode} [props.helperText] - Text shown below the input when there is no string `error`.
 * @param {boolean} [props.multiline=false] - Renders a <textarea> instead of an <input>.
 * @param {number} [props.rows] - Row count, only used when `multiline` is true.
 * @param {'outlined'|'filled'|'standard'} [props.variant='outlined'] - Visual style of the input.
 * @param {import('react').ReactNode} [props.startAdornment] - Content rendered inside the input's left edge.
 * @param {import('react').ReactNode} [props.endAdornment] - Content rendered inside the input's right edge.
 * @param {boolean} [props.floatingLabel=false] - If true, renders `label` as a floating label that shrinks above the input's notched outline when focused or a value is present, instead of a static label above the field.
 * @param {import('react').ReactNode} [props.startIcon] - Icon rendered inside the input, pinned to the left (e.g. a search icon). Purely decorative — not focusable or clickable.
 * @param {import('react').ReactNode} [props.endIcon] - Icon rendered inside the input, pinned to the right. Replaced by a loading spinner while `asyncValidate` is in flight.
 * @param {(value: string|number) => Promise<string|boolean|undefined>} [props.asyncValidate] - Async validator run (debounced) whenever `value` changes. Resolve with a string for an error message, or a falsy value if valid. Can run local checks before an `await`ed server call as two phases of the same call. While the returned promise is pending, the field shows a spinner at its end (the input stays enabled, so typing doesn't lose focus).
 * @param {number} [props.asyncValidateDebounceMs=300] - Debounce delay, in milliseconds, before `asyncValidate` is called after `value` stops changing.
 * @param {string} [props.asyncValidateErrorMessage] - Error message shown if the `asyncValidate` promise rejects (e.g. a network failure). Defaults to a generic message.
 * @param {string} [props.className] - Class for the outer wrapper.
 * @param {string} [props.inputClassName] - Class for the inner <input>.
 * @param {React.CSSProperties} [props.inputStyle] - Inline style for the inner <input>/<textarea>.
 * @param {string} [props.labelClassName] - Class for the <label>. With `floatingLabel`, also applies to the legend's mirrored label text.
 * @param {React.CSSProperties} [props.labelStyle] - Inline style for the <label> (e.g. to theme its color). With `floatingLabel`, also applies to the legend's mirrored label text.
 * @param {React.CSSProperties} [props.style] - Inline style for the outer wrapper. When `floatingLabel` is set, you can also pass a `--floating-label-bg` custom property here to override the white patch the shrunk label's background paints over the notched outline (defaults to `#fff`), e.g. for a dark theme.
 */
const TextField = React.forwardRef(({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  size = 'medium',
  disabled = false,
  fullWidth = false,
  error,
  helperText,
  multiline = false,
  rows,
  variant = 'outlined',
  startAdornment,
  endAdornment,
  floatingLabel = false,
  startIcon,
  endIcon,
  asyncValidate,
  asyncValidateDebounceMs = 300,
  asyncValidateErrorMessage = DEFAULT_ASYNC_VALIDATE_ERROR_MESSAGE,
  className,
  inputClassName,
  inputStyle,
  labelClassName,
  labelStyle,
  style,
  ...rest
}, ref) => {
  const [isValidating, setIsValidating] = useState(false);
  const [asyncError, setAsyncError] = useState(undefined);

  useEffect(() => {
    if (!asyncValidate) {
      return undefined;
    }

    let active = true;

    const debounceTimer = setTimeout(() => {
      setIsValidating(true);

      asyncValidate(value)
        .then((result) => {
          if (active) {
            setAsyncError(result);
            setIsValidating(false);
          }
        })
        .catch((err) => {
          console.error('Error running async validation:', err);
          if (active) {
            setAsyncError(asyncValidateErrorMessage);
            setIsValidating(false);
          }
        });
    }, asyncValidateDebounceMs);

    return () => {
      active = false;
      clearTimeout(debounceTimer);
    };
  }, [value, asyncValidate, asyncValidateDebounceMs, asyncValidateErrorMessage]);

  const effectiveError = error ?? asyncError;

  const fieldClassName = [
    styles.textFieldInput,
    size === 'small' && styles.textFieldSmall,
    effectiveError && styles.textFieldInputError,
    floatingLabel && styles.textFieldInputFloating,
    variant === 'filled' && styles.textFieldInputFilled,
    variant === 'standard' && styles.textFieldInputStandard,
    multiline && styles.textFieldInputMultiline,
    startAdornment && styles.textFieldInputPaddedStart,
    endAdornment && styles.textFieldInputPaddedEnd,
    startIcon && styles.textFieldInputWithStartIcon,
    (endIcon || isValidating) && styles.textFieldInputWithEndIcon,
    inputClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const Element = multiline ? 'textarea' : 'input';

  const inputNode = (
    <Element
      ref={ref}
      className={fieldClassName}
      style={inputStyle}
      type={multiline ? undefined : type}
      rows={multiline ? rows : undefined}
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.value, e)}
      {...rest}
    />
  );

  const renderEndSlot = () => {
    if (isValidating) {
      return <span className={styles.textFieldEndIcon}><Spinner size={16} /></span>;
    }
    if (endIcon) {
      return <span className={styles.textFieldEndIcon}>{endIcon}</span>;
    }
    return null;
  };

  // Always the same wrapper, regardless of startIcon/endIcon/adornments/isValidating —
  // toggling between a bare `<input>` and one wrapped in a `<div>` at the same
  // tree position makes React remount the input (losing focus) every time
  // those props change, e.g. when async validation starts.
  const controlNode = (
    <div className={styles.textFieldAdornmentWrapper}>
      {startIcon && <span className={styles.textFieldStartIcon}>{startIcon}</span>}
      {startAdornment && (
        <span className={styles.textFieldAdornmentStart}>{startAdornment}</span>
      )}
      {inputNode}
      {endAdornment && (
        <span className={styles.textFieldAdornmentEnd}>{endAdornment}</span>
      )}
      {renderEndSlot()}
    </div>
  );

  if (floatingLabel) {
    return controlNode;
  }

  const wrapperClassName = [
    styles.textField,
    fullWidth && styles.textFieldFull,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const hasError = Boolean(effectiveError);
  const errorMessage = typeof effectiveError === 'string' && effectiveError ? effectiveError : null;
  const helperMessage = errorMessage || helperText;

  return (
    <div className={wrapperClassName} style={style}>
      {label != null && (
        <label
          className={[styles.textFieldLabel, labelClassName].filter(Boolean).join(' ')}
          style={labelStyle}
        >
          {label}
        </label>
      )}
      {controlNode}
      {helperMessage != null && helperMessage !== '' && (
        <span className={hasError ? styles.textFieldError : styles.textFieldHelperText}>
          {helperMessage}
        </span>
      )}
    </div>
  );
});

TextField.floatingLabelLeft = '10px';

export default withFloatingLabel(TextField);
export { TextField as TextFieldBase };
